import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, Divider, List, ListItemButton, ListItemText, Stack,
  TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSearch } from '../../api/hooks';
import { StageChip, UserText } from '../common/Common';

export function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const navigate = useNavigate();
  const { data, isFetching } = useSearch(debounced);

  useEffect(() => {
    if (!open) {
      setTerm('');
      setDebounced('');
    }
  }, [open]);

  // Without this the dialog fires a request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), 200);
    return () => clearTimeout(timer);
  }, [term]);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const total = (data?.customers.length ?? 0) + (data?.contacts.length ?? 0) + (data?.opportunities.length ?? 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder={t('search.placeholder')}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          inputProps={{ dir: 'auto', 'data-testid': 'search-input', maxLength: 100 }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
        />

        {term.trim().length > 0 && (
          <List sx={{ mt: 1 }} data-testid="search-results">
            {total === 0 && !isFetching && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3 }}>
                {t('search.noMatch', { term })}
              </Typography>
            )}

            {data?.customers.length ? <Group label={t('search.customers')} /> : null}
            {data?.customers.map((c) => (
              <ListItemButton key={`c${c.id}`} onClick={() => go(`/customers/${c.id}`)}>
                <ListItemText
                  primary={<UserText>{c.name}</UserText>}
                  secondary={c.country ?? undefined}
                />
              </ListItemButton>
            ))}

            {data?.contacts.length ? <Group label={t('search.contacts')} /> : null}
            {data?.contacts.map((c) => (
              <ListItemButton key={`p${c.id}`} onClick={() => go(`/customers/${c.customerId}`)}>
                <ListItemText
                  primary={<UserText>{c.name}</UserText>}
                  secondary={<UserText>{[c.email, c.customerName].filter(Boolean).join(' · ')}</UserText>}
                />
              </ListItemButton>
            ))}

            {data?.opportunities.length ? <Group label={t('search.opportunities')} /> : null}
            {data?.opportunities.map((o) => (
              <ListItemButton key={`o${o.id}`} onClick={() => go(`/opportunities/${o.id}`)}>
                <ListItemText
                  primary={<UserText>{o.title}</UserText>}
                  secondary={<UserText>{o.customerName}</UserText>}
                />
                <StageChip stage={o.stage} />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Group({ label }: { label: string }) {
  return (
    <Stack sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Divider />
    </Stack>
  );
}
